import { RootState } from "../../../redux-store";
import { selectVehicles } from "../trailers/selectors";
import { createSelector } from "@reduxjs/toolkit";
import { Trailer } from "../trailers/types";
import { VehicleForLiveView } from "./types";
import groupBy from "lodash/groupBy";

const selectTrucksWithTrackers = createSelector(selectVehicles, (trailers) =>
  // Belongs to src/ducks/data/trailers, but created here unexported
  // because it is untested, and used for live view only
  trailers.filter((x) => x.trackerDeviceId)
);

export const selectFilterTeamIds = (state: RootState) =>
  state.data.liveView.filterTeamIds;

export const selectVehiclesForLiveView = createSelector(
  selectTrucksWithTrackers,
  selectFilterTeamIds,
  (state: RootState) => state.data.liveView.vehiclesForLiveView,
  (
    trucks,
    filteredTeamIds,
    vehiclesForLiveView
  ): {
    id: string;
    truck: Trailer;
    liveViewSession?: VehicleForLiveView;
  }[] => {
    const liveViews = groupBy(vehiclesForLiveView, (x) => x.truckId);
    return trucks
      .filter((truck) => truck.teams.some((x) => filteredTeamIds.includes(x)))
      .map((truck) =>
        (liveViews[truck.id] || []).map((liveView) => ({
          id: liveView.routeId || "",
          truck,
          liveViewSession: liveView,
        }))
      )
      .flat();
  }
);

// The routes drivers are currently on, whose live view rooms the overview
// listens to (live-view-route).
export const selectLiveViewRouteIds = createSelector(
  (state: RootState) => state.data.liveView.vehiclesForLiveView,
  (vehiclesForLiveView) =>
    vehiclesForLiveView.flatMap((x) => (x.routeId ? [x.routeId] : []))
);

// Memoised: the route page reads it with useSelector, and a new object for
// the same state would re-render the page on every store change.
export const selectTruckRouteForLiveView = createSelector(
  (state: RootState) => state.data.liveView.routeForLiveView,
  (state: RootState) => state.data.trailers.entities,
  (state: RootState) => state.data.drivers.driverEntities,
  (routeForLiveView, vehicles, drivers) => {
    if (!routeForLiveView) {
      return null;
    }
    const { vehicle } = routeForLiveView;
    const truck = vehicles[vehicle.truckId];
    const trailer = vehicle.trailerIds[0]
      ? vehicles[vehicle.trailerIds[0]]
      : undefined;
    const driver = vehicle.driverId ? drivers[vehicle.driverId] : undefined;

    return {
      truckNumber: truck?.number,
      truckId: vehicle.truckId,
      trailerNumber: trailer?.number,
      driverName: driver ? `${driver.firstName} ${driver.lastName[0]}.` : null,
      driverPhone: driver
        ? `${driver.phoneNo.countryCode}${driver.phoneNo.number}`
        : null,
      driverStartDayDate: routeForLiveView.driverStartDayDate,
      routeId: vehicle.routeId,
      pastLocations: routeForLiveView.pastLocations,
      futureLocations: routeForLiveView.futureLocations,
    };
  }
);

export const selectRouteForLiveViewEnded = (state: RootState) =>
  state.data.liveView.routeForLiveViewEnded;

export const selectTrackerDeviceMessagesForLiveView = (state: RootState) =>
  state.data.liveView.routeForLiveViewFlespiMessages;
